import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Building2, Check, ChevronDown, RefreshCw } from 'lucide-react';
import { useTenant } from '../hooks/useTenant';
import { tenantsAPI } from '../services/api';
import { tenantRoleLabel } from '../config/tenantRoles';

export const TenantSwitcher = ({ placement = 'header', collapsed = false }) => {
  const { tenants, activeTenant, loading, setActiveTenant, refreshTenants } = useTenant();
  const [open, setOpen] = useState(false);
  const [switchingId, setSwitchingId] = useState(null);
  const [error, setError] = useState('');
  const [menuPosition, setMenuPosition] = useState(null);
  const ref = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClick = (event) => {
      const clickedSwitcher = ref.current?.contains(event.target);
      const clickedMenu = menuRef.current?.contains(event.target);
      if (!clickedSwitcher && !clickedMenu) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = async (tenant) => {
    if (!tenant || tenant.id === activeTenant?.id) {
      setOpen(false);
      return;
    }

    const previous = activeTenant;
    setError('');
    setSwitchingId(tenant.id);
    setActiveTenant(tenant);
    try {
      await tenantsAPI.switchTenant({ tenantId: tenant.id });
      setOpen(false);
    } catch (err) {
      setActiveTenant(previous || null);
      setError(err?.message || 'Could not switch organisation');
    } finally {
      setSwitchingId(null);
    }
  };

  const label = loading
    ? 'Loading organisations'
    : activeTenant?.name || activeTenant?.slug || 'No organisation';

  const isSidebar = placement === 'sidebar';
  const useFixedSidebarMenu = isSidebar && collapsed;
  const updateMenuPosition = useCallback(() => {
    if (!useFixedSidebarMenu || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setMenuPosition({
      left: Math.round(rect.right + 10),
      bottom: Math.max(12, Math.round(window.innerHeight - rect.top + 8)),
    });
  }, [useFixedSidebarMenu]);

  useEffect(() => {
    if (!open || !useFixedSidebarMenu) {
      setMenuPosition(null);
      return undefined;
    }

    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [open, updateMenuPosition, useFixedSidebarMenu]);

  const buttonClass = isSidebar
    ? [
        'flex w-full items-center gap-2 min-h-11 rounded-xl border-0 appearance-none bg-white/[0.07]',
        'text-white/90 shadow-none hover:bg-white/[0.11] transition-colors outline-none focus-ring',
        collapsed ? 'justify-center px-0' : 'px-3',
      ].join(' ')
    : 'hidden md:flex btn-header-pill focus-ring outline-none';
  const menuClass = useFixedSidebarMenu
    ? 'fixed z-[60] w-[236px] rounded-2xl overflow-hidden bg-white shadow-[0_12px_48px_rgba(0,0,0,0.24)]'
    : isSidebar
      ? 'absolute left-0 bottom-full mb-2 z-50 w-[236px] rounded-2xl overflow-hidden bg-white shadow-[0_12px_48px_rgba(0,0,0,0.24)]'
      : 'absolute right-0 top-full mt-2 z-50 w-[320px] rounded-2xl overflow-hidden bg-white shadow-[0_12px_48px_rgba(0,0,0,0.14)]';
  const menuStyle = useFixedSidebarMenu && menuPosition ? menuPosition : undefined;
  const iconClass = isSidebar ? 'text-[#6BAF65] flex-shrink-0' : 'text-lb-green-primary flex-shrink-0';
  const titleClass = isSidebar ? `${collapsed ? 'sr-only' : 'max-w-[145px] truncate'} text-[12px] font-bold text-white` : 'max-w-[145px] truncate text-[12px] font-bold text-lb-ink';
  const roleClass = isSidebar ? `${collapsed ? 'sr-only' : 'max-w-[145px] truncate'} text-[10px] font-semibold text-white/55 capitalize mt-0.5` : 'max-w-[145px] truncate text-[10px] font-semibold text-lb-muted capitalize mt-0.5';
  const chevronClass = isSidebar ? `${collapsed ? 'hidden' : 'text-white/55 flex-shrink-0 ml-auto'}` : 'text-lb-muted flex-shrink-0';

  if (!loading && tenants.length === 0) return null;

  const menu = open ? (
    <div
      ref={menuRef}
      className={menuClass}
      style={menuStyle}
      role="menu"
      aria-label="Organisation options"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-black/[0.06]">
        <span className="text-[13px] font-bold text-lb-ink">Active organisation</span>
        <button
          type="button"
          onClick={refreshTenants}
          className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-lb-muted hover:text-lb-ink hover:bg-black/[0.04] outline-none focus-ring"
          aria-label="Refresh organisations"
        >
          <RefreshCw size={13} strokeWidth={2} />
        </button>
      </div>

      <div className="max-h-[280px] overflow-y-auto py-1">
        {loading ? (
          <div className="px-4 py-4 text-[12px] text-lb-muted">Loading memberships...</div>
        ) : (
          tenants.map((tenant) => {
            const selected = tenant.id === activeTenant?.id;
            const switching = switchingId === tenant.id;
            return (
              <button
                key={tenant.id}
                type="button"
                role="menuitem"
                onClick={() => handleSelect(tenant)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left border-0 rounded-none bg-transparent
                           hover:bg-black/[0.035] transition-colors outline-none focus-ring"
              >
                <span
                  className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 text-[11px] font-bold text-white"
                  style={{ background: selected ? '#3D6B38' : '#7A8B77' }}
                >
                  {(tenant.name || tenant.slug || 'T').slice(0, 1).toUpperCase()}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[13px] font-bold text-lb-ink truncate">
                    {tenant.name || tenant.slug}
                  </span>
                  <span className="block text-[11px] font-semibold text-lb-muted capitalize truncate">
                    {tenantRoleLabel(tenant.role)}
                    {tenant.isDefault ? ' - default' : ''}
                  </span>
                </span>
                {switching ? (
                  <RefreshCw size={14} className="animate-spin text-lb-muted flex-shrink-0" />
                ) : selected ? (
                  <Check size={15} strokeWidth={2.2} className="text-lb-green-primary flex-shrink-0" />
                ) : null}
              </button>
            );
          })
        )}
      </div>

      {error && (
        <div className="px-4 py-2 text-[12px] font-semibold text-red-700 bg-red-50 border-t border-red-100">
          {error}
        </div>
      )}
    </div>
  ) : null;

  return (
    <div className="relative" ref={ref}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        className={buttonClass}
        aria-label="Switch organisation"
        aria-expanded={open}
      >
        <Building2 size={15} strokeWidth={2} className={iconClass} />
        <span className={collapsed && isSidebar ? 'sr-only' : 'min-w-0 flex flex-col items-start leading-none'}>
          <span className={titleClass}>
            {label}
          </span>
          {activeTenant?.role && (
            <span className={roleClass}>
              {tenantRoleLabel(activeTenant.role)}
            </span>
          )}
        </span>
        <ChevronDown size={14} strokeWidth={2} className={chevronClass} />
      </button>

      {useFixedSidebarMenu && typeof document !== 'undefined'
        ? createPortal(menu, document.body)
        : menu}
    </div>
  );
};

export default TenantSwitcher;
